// @ts-strict-ignore
import { createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import * as UserActions from '../actions/employee.actions';
import { Employee, EmployeeGroup, User } from '@fc-core';
import {
  FilterGroup,
  FilterItem,
} from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import { changeSortByListOrder } from '@fc-shared/utils/change-sort-by-list-order';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';

export const employeesFeatureKey = 'employees';

export interface State extends EntityState<Employee> {
  loading: boolean;
  loaded: boolean;
  selectedUserGroupFilter: EmployeeGroup;
  sortBy: SortBy;
  sortByList: SortBy[];
  searchText: string;
  usersCount: number;
  usersProgress: number;
  invitesProgress: number;
  userError: any;
  userFilters: FilterGroup[];
  selectedFilters: FilterItem[];
  usersList: User[];
  kioskPin: string;
  currentEmployee: Employee;
}

export const adapter: EntityAdapter<Employee> = createEntityAdapter<Employee>();

export const initialState: State = adapter.getInitialState({
  loaded: false,
  loading: false,
  selectedUserGroupFilter: { name: 'All employees', id: -1, color: '' },
  sortBy: { name: 'First Name', value: 'firstName', order: false },
  sortByList: [
    { name: 'First name', value: 'firstName', order: false },
    { name: 'Last name', value: 'lastName', order: null },
  ],
  searchText: '',
  usersCount: 0,
  usersProgress: 0,
  invitesProgress: 0,
  userError: 0,
  userFilters: [
    {
      name: 'Filters',
      filters: [
        { name: 'Operator', id: 'id_operator', type: 'operator' },
        { name: 'User', id: 'id_user', type: 'user' },
      ],
    },
  ],
  selectedFilters: [],
  usersList: [],
  kioskPin: null,
  currentEmployee: null,
});

export const reducer = createReducer(
  initialState,
  on(
    UserActions.loadEmployees,
    UserActions.linkEmployee,
    UserActions.enableOperator,
    UserActions.disableOperator,
    UserActions.enableAdmin,
    UserActions.disableAdmin,
    UserActions.unarchiveEmployee,
    (state) => ({
      ...state,
      loading: true,
    }),
  ),
  on(UserActions.createEmployeeWithInvite, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.loadEmployeesSuccess, (state, action) => ({
    ...adapter.addMany(action.users, state),
    loading: false,
    usersCount: action.count,
  })),
  on(UserActions.selectUserGroupFilter, (state, action) => ({
    ...state,
    selectedUserGroupFilter: action.userGroup,
  })),
  on(UserActions.setEmployeeSortBy, (state, action) => ({
    ...state,
    sortBy: {
      ...action.sortBy,
      order: action.sortBy === null ? true : !action.sortBy.order,
    },
    sortByList: changeSortByListOrder(state.sortByList, action.sortBy),
  })),
  on(UserActions.setSearchText, (state, action) => ({
    ...state,
    searchText: action.searchText,
  })),

  on(UserActions.addEmployee, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.unlinkEmployeeSuccess, (state, action) => ({
    ...state,
    ...adapter.updateOne(
      {
        changes: {
          ...(adapter.getSelectors() as any)[action.id],
          user: null,
        },
        id: action.id,
      },
      state,
    ),
  })),
  on(UserActions.addEmployeeSuccess, (state, action) => ({
    ...adapter.addOne(action.employee, state),
    loading: false,
    usersCount: state.usersCount + 1,
  })),
  on(UserActions.updateEmployee, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.loadEmployeeSuccess, (state, action) => ({
    ...state,
    ...adapter.updateOne(
      {
        id: action.employee.id,
        changes: action.employee,
      },
      state,
    ),
  })),
  on(UserActions.employeeAPIError, (state, action) => ({
    ...state,
    userError: action.error,
    loading: false,
  })),
  on(UserActions.archiveEmployee, (state) => ({
    ...state,
    loading: true,
  })),
  on(UserActions.archiveEmployeeSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        id: action.employeeId,
        changes: {
          archived: true,
          operator: null,
          archivedOn: new Date().toISOString(),
        },
      },
      state,
    ),
    loading: false,
  })),
  on(UserActions.unarchiveEmployeeSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        id: action.employeeId,
        changes: { archived: false },
      },
      state,
    ),
    loading: false,
  })),
  on(UserActions.updateEmployeeSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: action.employee,
        id: action.employee.id,
      },
      state,
    ),
    currentEmployee:
      state.currentEmployee?.id === action.employee.id
        ? { ...state.currentEmployee, ...action.employee }
        : state.currentEmployee,
    loading: false,
  })),
  on(UserActions.setManyEmployeeFilters, (state, action) => ({
    ...state,
    selectedFilters: action.filters,
  })),
  on(UserActions.enableOperatorSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: { operator: action.employee.operator },
        id: action.employee.id,
      },
      state,
    ),
    loading: false,
  })),
  on(UserActions.disableOperatorSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: { operator: null },
        id: action.employeeId,
      },
      state,
    ),
    loading: false,
  })),
  on(UserActions.disableAdminSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: { isAdmin: null },
        id: action.employee.id,
      },
      state,
    ),
    loading: false,
  })),
  on(UserActions.enableAdminSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        changes: { isAdmin: action.employee.isAdmin },
        id: action.employee.id,
      },
      state,
    ),
    loading: false,
  })),
  on(UserActions.resetEmployeeFilters, (state) => ({
    ...state,
    selectedFilters: [] as FilterItem[],
    selectedUserGroupFilter: { name: 'All users', id: -1, color: '' },
  })),
  on(UserActions.loadCurrentEmployeeSuccess, (state, { employee }) => ({
    ...state,
    currentEmployee: employee,
  })),
  on(UserActions.setEmployeeFilter, (state, action) => ({
    ...state,
    selectedFilters: state.selectedFilters
      .map((i) => i.id)
      .includes(action.filterItem.id)
      ? state.selectedFilters.filter(
          (filter) => filter.id !== action.filterItem.id,
        )
      : [...state.selectedFilters, action.filterItem],
  })),
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const getSelectedUserGroupFilter = (state: State) =>
  state.selectedUserGroupFilter;
export const getUserError = (state: State) => state.userError;
export const getUserFilters = (state: State) => state.userFilters;
export const getSelectedEmployeeFilters = (state: State) =>
  state.selectedFilters;
export const getSortBy = (state: State) => state.sortBy;
export const getSortByList = (state: State) => state.sortByList;
export const getLoading = (state: State) => state.loading;
export const getSearchText = (state: State) => state.searchText;
export const getUsersCount = (state: State) => state.usersCount;
export const getCurrentEmployee = (state: State) => state.currentEmployee;
export const getUsersKioskPin = (state: State) => state.kioskPin;
